import styled, { keyframes } from "styled-components";
import { styled as muiStyled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControlLabel from '@mui/material/FormControlLabel';

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

export const SavingText = styled.span`
  animation: ${pulse} 1s ease-in-out infinite;
`;

export const SavedText = styled.span`
  animation: ${pulse} 0.5s ease-in-out;
`;

export const EditableTitle = styled.textarea.attrs({
  className: 'EventTitle'
})`
  margin: 0;
  margin-left: 8px;
  font-size: 14px;
  line-height: 18px;
  font-weight: 600;
  width: 100%;
  font-family: Arial;
  border: none;
  background: transparent;
  padding: 0;
  resize: none;
  overflow: hidden;
  min-height: 18px;
  height: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  
  &:focus {
    outline: none;
  }
  &::placeholder {
    color: #999;
  }
`;

export const EditableDescription = styled.textarea`
  width: 100%;
  border: none;
  padding: 0;
  margin: 0;
  font-size: 14px;
  line-height: 17px;
  resize: none;
  background: transparent;
  height: auto;
  overflow: hidden;
  color: #333;
  font-weight: 600;
  margin-left: 5px;
  font-family: pt sans;
  background: #f7f7f7;
  border-radius: 3px;
  padding: 10px 12px 15px;
  min-height: 50px;
  margin-left: -3px;

  box-sizing: border-box;
  &:focus {
    outline: none;
  }
  &::placeholder {
    color: #999;
  }

  &.EditableContent {
    min-height: 100px;
    background: #e9e9e9;
    font-weight: 400;
  }
`;

export const EditableDateTime = styled.div`
  display: flex;
  gap: 8px;
  margin-left: 3px;
  background: #e9e9e9;
  padding: 5px 10px;
  border-radius: 2px;
  flex-wrap: wrap;
  input {
    padding: 0;
    border: none;
    background: transparent;
    font-size: 13px;
    color: #666;
    &:focus {
      outline: none;
    }
  }
`;

export const EditableImage = styled.div`
  position: relative;
  width: 98px;
  height: 80px;
  cursor: pointer;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 3px;
  }
`;

export const ImageInput = styled.input`
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 12px;
  background: white;
  margin-top: 5px;
  
  &:focus {
    outline: none;
    border-color: #666;
  }

  &::placeholder {
    color: #999;
  }
`;

export const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 11px;
  opacity: 0;
  transition: opacity 0.2s;
  border-radius: 3px;
  ${EditableImage}:hover & {
    opacity: 1;
  }
`;

export const EditingContainer = styled.div`
  margin-top: 0px;
  background: white;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  padding: 5px;
  margin-top: 5px;
`;

export const SaveButton = muiStyled(Button)`
  padding: 5px 10px;
  text-transform: none;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
  background-color: rebeccapurple;
  color: white;
  border: none;
  border-radius: 3px 0px 0px 3px;
  flex: 1;
  &:hover {
    background-color: rebeccapurple;
    opacity: 0.9;
  }
  
  &:disabled {
    background-color: #cccccc;
    color: #666666;
  }
`;

export const StyledDropdownButton = muiStyled(Button)`
  padding: 10px 10px;
  text-transform: none;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
  background-color: rebeccapurple;
  color: white;
  border: none;
  min-width: 40px;
  justify-content: center;
  border-left: 1px solid rgba(255, 255, 255, 0.3);
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  margin-left: -8px;
  span { 
    margin-left: 0px;
  }
  &:hover {
    background-color: rebeccapurple;
    opacity: 0.9;
  }
  
  &:disabled {
    background-color: #cccccc;
    color: #666666;
  }
`;

export const StyledMenu = muiStyled(Menu)`
  .MuiPaper-root {
    box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    position: fixed;
    margin-top: -165px !important;
  }
  
  &.MuiMenu-root {
    .MuiPaper-root {
      transform-origin: bottom center !important;
    }
  }
`;

export const StyledMenuItem = muiStyled(MenuItem, {
  shouldForwardProp: prop => !prop.startsWith('$')
})`
  padding: 8px 15px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
  display: flex;
  justify-content: flex-end;
  color: ${props => props.$isDestructive ? '#F44336' : 'inherit'};
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
  
  .menu-item-content {
    display: flex;
    align-items: center;
  }
`;

export const CancelButton = styled.button`
  padding: 6px 12px;
  border-radius: 3px;
  background-color: #f1f1f1;
  border: none;
  cursor: pointer;
  font-size: 13px;
  
  &:hover {
    background-color: #e1e1e1;
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

export const EditingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0;
  max-width: 600px;
  div {
      flex-wrap: wrap;
      display: flex;
  }
  &:hover {
    div {
      color: rebeccapurple;
    }
  }

  &:first-of-type {
    padding-top: 0;
  }

  &:not(:last-child) {
    // border-bottom: 1px solid #eee;
  }
`;

export const ResourceItem = styled.div`
  display: grid;
  grid-template-columns: 2fr 3fr auto;
  gap: 8px;
  align-items: center;
  
  input {
    padding: 6px 8px;
    border: 1px solid #ddd;
    border-radius: 3px;
    font-size: 12px;
    background: white;
    
    &:focus {
      outline: none;
      border-color: #666;
    }

    &::placeholder {
      color: #999;
    }
  }
`;

export const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: none;
  border: 1px dashed #999;
  padding: 4px 8px;
  border-radius: 3px;
  color: #666;
  cursor: pointer;
  font-size: 11px;
  margin-top: 4px;
  width: fit-content;
  
  &:hover {
    background: #f0f0f0;
    border-color: #666;
  }

  svg {
    font-size: 14px;
  }
`;

export const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  
  &:hover {
    color: #dc2626;
  }

  svg {
    font-size: 16px;
  }
`;

export const HostsSelect = styled(Select)`
  .MuiSelect-select {
    padding: 6px 8px;
    font-size: 12px;
    min-height: unset !important;
    background: white;
  }

  .MuiOutlinedInput-notchedOutline {
    border-color: #ddd;
  }

  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: #999;
  }

  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #666;
    border-width: 1px;
  }
`;

export const ActionButton = styled.button`
  padding: 5px 10px;
  border-radius: 3px;
  background-color: rebeccapurple;
  border: none;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
  color: white;
  flex: 1;
  &:hover {
    background-color: rebeccapurple;
    opacity: 0.9;
  }
  
  &:disabled {
    background-color: #cccccc;
    color: #666666;
  }
`;

export const RecurrenceOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
  padding-left: 16px;
  .MuiSelect-select {
    padding: 6px 8px;
    font-size: 12px;
    min-height: unset !important;
    background: white;
    }

  input[type="number"],
  input[type="date"] {
    padding: 6px 8px;
    border: 1px solid #ddd;
    border-radius: 3px;
    font-size: 12px;
    background: white;
    width: 120px;

    &:focus {
      outline: none;
      border-color: #666;
    }
  }
`;

export const DaySelect = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin: 4px 0;
`;

export const DayButton = styled.button`
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 3px;
  background: ${props => props.$selected ? '#333' : 'white'};
  color: ${props => props.$selected ? 'white' : '#333'};
  cursor: pointer;
  font-size: 11px;
  
  &:hover {
    background: ${props => props.$selected ? '#444' : '#f0f0f0'};
  }
`;

export const ZoomInput = styled.input`
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 12px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #666;
  }

  &::placeholder {
    color: #999;
  }
`;

export const StyledFormControlLabel = styled(FormControlLabel)`
  .MuiFormControlLabel-label {
    font-size: 12px;
  }

  .MuiCheckbox-root {
    padding: 4px;
  }
`;

export const TabContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
`;

export const Tab = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 15px 8px 10px;
  border: none;
  border-radius: 3px;
  cursor: default;
  font-size: 12px;
  font-weight: ${props => props.$active ? '600' : '600'};
  transition: all 0.2s;
  background: ${props => props.$active ? 'rebeccapurple' : '#f7f7f7'};
  color: ${props => props.$active ? 'white' : '#666'};

  &:hover {
    ${props => !props.$active && `
      color: black;
    `}
  }

  svg {
    font-size: 14px;
  }
`;

export const ButtonGroup = styled.div.attrs({
  className: 'ButtonGroup'
})`
  display: flex;
  flex-direction: row;
  margin-top: 20px;
  width: 100%;
  gap: 8px;
`;